/**
 * Aviso único de "a mudança foi para aprovação, e nada aconteceu ainda".
 *
 * Existe para que os cinco pontos do ZTNA que passam pelo gate de mudança
 * digam a MESMA coisa. Cinco mensagens escritas à mão divergem em silêncio —
 * foi exatamente assim que os cinco pontos do WAF nasceram com cinco condições
 * ligeiramente diferentes, defeito citado no próprio código do backend
 * (`ztna_onboarding/change_control.py`) como o erro a não repetir.
 *
 * A frase "Nada foi alterado ainda" não é enfeite: sem ela o operador lê
 * "registrada", assume que a mudança valeu, vê a tela com o estado antigo e
 * clica de novo. Nos caminhos de job o backend recusa o segundo clique com
 * 409; nos de intenção (toggle, postura, edição) ele **aceita** e abre uma
 * segunda GMUD, por decisão documentada. Então quem evita o clique repetido
 * aqui é o texto.
 */
import { notifications } from '@mantine/notifications'

/** Rótulo curto da ação, em minúsculas, para caber na frase. */
export type AcaoGmud =
  | 'a suspensão'
  | 'a reativação'
  | 'a mudança de postura'
  | 'a edição'
  | 'a exclusão'
  | 'o provisionamento'

/** O que fazer com a GMUD que a ação abriu. Compartilhado com as telas de WAF. */
export const PROXIMO_PASSO_GMUD =
  'Abra a GMUD em Chamados → GMUD, informe a janela e envie para aprovação.'

export function notifyPendingGmud(
  acao: AcaoGmud,
  alvo: string,
  gmudId?: number | null,
): void {
  const ref = gmudId != null ? `GMUD #${gmudId}` : 'uma GMUD'
  notifications.show({
    color: 'blue',
    title: 'Aguardando aprovação',
    // A GMUD nasce em RASCUNHO: sem alguém informar a janela e enviar, ninguém a
    // aprova e o pedido fica em `pending_gmud` para sempre (consoleai#22923). A
    // frase do próximo passo é o que tira o operador da espera.
    message:
      `${acao.charAt(0).toUpperCase()}${acao.slice(1)} de ${alvo} exige ` +
      `aprovação e foi registrada em ${ref}. Nada foi alterado ainda. ` +
      PROXIMO_PASSO_GMUD,
    // Mais longo que o default: é a única pista que o operador tem de que a
    // ação não aconteceu, e ele precisa de tempo para ler o número.
    autoClose: 10_000,
  })
}

/**
 * `true` quando a resposta indica que a ação virou GMUD em vez de acontecer.
 *
 * Checa o `gmud_id` e não o `status`, porque só duas das cinco respostas têm
 * campo de status — o `gmud_id` é o que todas as cinco carregam.
 */
export function foiParaGmud(resp: { gmud_id?: number | null }): boolean {
  return resp.gmud_id != null
}
